package com.patronat.top.features.usermanagement.presentation

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.unit.dp
import com.patronat.top.dto.UserRole
import com.patronat.top.dto.UserStatus
import com.patronat.top.features.usermanagement.presentation.create.CreateUserScreen
import com.patronat.top.ui.molecules.filters.FilterOption
import com.patronat.top.ui.organisms.EmptyStateContent
import com.patronat.top.ui.organisms.UsersControlPanel
import com.patronat.top.ui.theme.*
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import top.features.user_management.generated.resources.Res
import top.features.user_management.generated.resources.btn_add_employee
import top.features.user_management.generated.resources.btn_add_moderator
import top.features.user_management.generated.resources.empty_moderators_desc
import top.features.user_management.generated.resources.empty_moderators_title
import top.features.user_management.generated.resources.empty_team_desc
import top.features.user_management.generated.resources.empty_team_title
import top.features.user_management.generated.resources.empty_users_desc
import top.features.user_management.generated.resources.empty_users_title
import top.features.user_management.generated.resources.filter_role_label
import top.features.user_management.generated.resources.filter_status_label
import top.features.user_management.generated.resources.img_empty_moderators
import top.features.user_management.generated.resources.modal_create_moderator_title
import top.features.user_management.generated.resources.role_business_owner
import top.features.user_management.generated.resources.role_client
import top.features.user_management.generated.resources.role_employee
import top.features.user_management.generated.resources.search_placeholder
import top.features.user_management.generated.resources.status_active
import top.features.user_management.generated.resources.status_blocked
import top.features.user_management.generated.resources.status_deactivated
import top.features.user_management.generated.resources.status_unauthorized
import top.features.user_management.generated.resources.status_unknown

private data class EmptyStateConfig(
    val image: Painter,
    val title: StringResource,
    val description: StringResource,
    val buttonText: StringResource,
    val buttonNeeded: Boolean,
    val targetRole: UserRole
)

@Composable
private fun rememberEmptyStateConfig(userRoles: List<UserRole>): EmptyStateConfig {
    val isModerators = userRoles.contains(UserRole.MODERATOR)
    val isTeam = userRoles.contains(UserRole.EMPLOYEE) && userRoles.size == 1
    val isUsers = userRoles.containsAll(listOf(UserRole.CLIENT, UserRole.BUSINESS_OWNER, UserRole.EMPLOYEE))

    return when {
        isModerators -> EmptyStateConfig(
            image = painterResource(Res.drawable.img_empty_moderators),
            title = Res.string.empty_moderators_title,
            description = Res.string.empty_moderators_desc,
            buttonText = Res.string.btn_add_moderator,
            buttonNeeded = true,
            targetRole = UserRole.MODERATOR
        )
        isTeam -> EmptyStateConfig(
            image = painterResource(Res.drawable.img_empty_moderators), // Placeholder
            title = Res.string.empty_team_title,
            description = Res.string.empty_team_desc,
            buttonText = Res.string.btn_add_employee,
            buttonNeeded = true,
            targetRole = UserRole.EMPLOYEE
        )
        isUsers -> EmptyStateConfig(
            image = painterResource(Res.drawable.img_empty_moderators), // Placeholder
            title = Res.string.empty_users_title,
            description = Res.string.empty_users_desc,
            buttonText = Res.string.btn_add_moderator, // Not used
            buttonNeeded = false,
            targetRole = UserRole.CLIENT // Default, not used
        )
        else -> EmptyStateConfig( // Default fallback
            image = painterResource(Res.drawable.img_empty_moderators), // Placeholder
            title = Res.string.empty_users_title,
            description = Res.string.empty_users_desc,
            buttonText = Res.string.btn_add_moderator,
            buttonNeeded = false,
            targetRole = UserRole.CLIENT
        )
    }
}


@Composable
actual fun UsersScreen(
    userRoles: List<UserRole>,
    onNavigateToCreate: () -> Unit,
    onNavigateToEdit: (String) -> Unit
) {
    val listViewModel = koinInject<UsersViewModel>()
    val listState by listViewModel.viewState.collectAsState()

    var showCreateModal by remember { mutableStateOf(false) }
    var userToEditId by remember { mutableStateOf<String?>(null) }

    var searchQuery by remember { mutableStateOf("") }
    var selectedStatuses by remember { mutableStateOf(emptyList<UserStatus>()) }
    var selectedRoles by remember { mutableStateOf(emptyList<UserRole>()) }

    val emptyStateConfig = rememberEmptyStateConfig(userRoles)
    val isUsersScreen = userRoles.containsAll(listOf(UserRole.CLIENT, UserRole.BUSINESS_OWNER, UserRole.EMPLOYEE))

    LaunchedEffect(Unit) {
        listViewModel.effect.collect { effect ->
            when (effect) {
                UsersEffect.NavigateToCreateUser -> showCreateModal = true
                is UsersEffect.NavigateToEditUser -> userToEditId = effect.userId
            }
        }
    }

    LaunchedEffect(userRoles) {
        listViewModel.onEvent(UsersEvent.OnLoadUsers(userRoles))
    }

    val statusLabels = mapOf(
        UserStatus.UNAUTHORIZED to stringResource(Res.string.status_unauthorized),
        UserStatus.ACTIVE to stringResource(Res.string.status_active),
        UserStatus.DEACTIVATED to stringResource(Res.string.status_deactivated),
        UserStatus.BLOCKED to stringResource(Res.string.status_blocked)
    )

    val statusFilterOptions = remember(statusLabels) {
        UserStatus.entries.mapNotNull { status ->
            statusLabels[status]?.let { label ->
                FilterOption(id = status.name, label = label)
            }
        }
    }

    val roleLabels = mapOf(
        UserRole.CLIENT to stringResource(Res.string.role_client),
        UserRole.BUSINESS_OWNER to stringResource(Res.string.role_business_owner),
        UserRole.EMPLOYEE to stringResource(Res.string.role_employee)
    )

    val roleFilterOptions = remember(roleLabels) {
        listOf(UserRole.CLIENT, UserRole.BUSINESS_OWNER, UserRole.EMPLOYEE).mapNotNull { role ->
            roleLabels[role]?.let { label ->
                FilterOption(id = role.name, label = label)
            }
        }
    }

    val currentSelectedStatusOptions = remember(selectedStatuses, statusFilterOptions) {
        statusFilterOptions.filter { option -> selectedStatuses.any { it.name == option.id } }
    }

    val currentSelectedRoleOptions = remember(selectedRoles, roleFilterOptions) {
        roleFilterOptions.filter { option -> selectedRoles.any { it.name == option.id } }
    }


    Box(modifier = Modifier.fillMaxSize().background(MineShaft50)) {
        if (listState.isEmpty && searchQuery.isEmpty() && selectedStatuses.isEmpty() && selectedRoles.isEmpty()) {
            EmptyStateContent(
                image = emptyStateConfig.image,
                title = stringResource(emptyStateConfig.title),
                description = stringResource(emptyStateConfig.description),
                buttonText = stringResource(emptyStateConfig.buttonText),
                onButtonClick = { listViewModel.onEvent(UsersEvent.OnAddUserClicked) },
                buttonIcon = AppIcons.Plus,
                buttonNeeded = emptyStateConfig.buttonNeeded
            )
        } else {
            Column(modifier = Modifier.background(Color.White)) {

                UsersControlPanel(
                    searchQuery = searchQuery,
                    onSearchQueryChange = { query ->
                        searchQuery = query
                        listViewModel.onEvent(UsersEvent.OnSearch(query))
                    },
                    searchPlaceholder = stringResource(Res.string.search_placeholder),

                    filterLabel = stringResource(if (isUsersScreen) Res.string.filter_role_label else Res.string.filter_status_label),
                    filterOptions = if (isUsersScreen) roleFilterOptions else statusFilterOptions,
                    selectedFilterOptions = if (isUsersScreen) currentSelectedRoleOptions else currentSelectedStatusOptions,
                    onFilterChange = { newOptions ->
                        if (isUsersScreen) {
                            selectedRoles = newOptions.map { UserRole.valueOf(it.id) }
                            listViewModel.onEvent(UsersEvent.OnRoleFilterChanged(selectedRoles))
                        } else {
                            selectedStatuses = newOptions.map { UserStatus.valueOf(it.id) }
                            listViewModel.onEvent(UsersEvent.OnFilterChanged(selectedStatuses))
                        }
                    },

                    filterLabel2 = if (isUsersScreen) stringResource(Res.string.filter_status_label) else null,
                    filterOptions2 = if (isUsersScreen) statusFilterOptions else null,
                    selectedFilterOptions2 = if (isUsersScreen) currentSelectedStatusOptions else null,
                    onFilterChange2 = if (isUsersScreen) {
                        { newOptions ->
                            selectedStatuses = newOptions.map { UserStatus.valueOf(it.id) }
                            listViewModel.onEvent(UsersEvent.OnFilterChanged(selectedStatuses))
                        }
                    } else null,

                    onAddClick = { listViewModel.onEvent(UsersEvent.OnAddUserClicked) },
                    addButtonText = stringResource(emptyStateConfig.buttonText),
                    isAddButtonVisible = !isUsersScreen,
                    modifier = Modifier.padding(bottom = 24.dp)
                )


                UsersTable(
                    users = listState.users,
                    totalCount = listState.totalCount,
                    currentPage = listState.currentPage,
                    pageSize = listState.pageSize,
                    expandedUserMenu = listState.expandedMenuUser,
                    onPageChange = { page -> listViewModel.onEvent(UsersEvent.OnPageChanged(page)) },
                    onPageSizeChange = { pageSize -> listViewModel.onEvent(UsersEvent.OnPageSizeChanged(pageSize)) },
                    onActionClick = { user -> listViewModel.onEvent(UsersEvent.OnUserAction(user)) },
                    onDismissMenu = { listViewModel.onEvent(UsersEvent.OnDismissMenu) },
                    onEditProfile = { user -> listViewModel.onEvent(UsersEvent.OnEditProfile(user)) },
                    onBlockUser = { user -> listViewModel.onEvent(UsersEvent.OnBlockUser(user)) },
                    onUnblockUser = { user -> listViewModel.onEvent(UsersEvent.OnUnblockUser(user)) },
                    isUsersScreen = isUsersScreen,
                    modifier = Modifier.fillMaxSize()
                )
            }
        }

        if (showCreateModal || userToEditId != null) {
            CreateUserScreen(
                modalTitle = if (userToEditId == null) "Створити користувача" else "Редагувати профіль",
                targetRole = emptyStateConfig.targetRole,
                userId = userToEditId,
                onBack = {
                    showCreateModal = false
                    userToEditId = null
                    listViewModel.onEvent(UsersEvent.OnLoadUsers(userRoles)) // Reload list
                }
            )
        }
    }
}